// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createApi } from '@reduxjs/toolkit/query/react';
import { fetchQuery } from '@shared/data-access/queries';
import { SHARED_TAG_TYPES } from '@shared/data-access/utils';
import { environment } from '@shared/environment';

/**
 * Brokerage tag types enum.
 *
 * Centralized enum for all brokerage-related cache tag types used in RTK Query V2.
 * This ensures type safety and makes it easy to refactor tag names across the
 * entire V2 codebase.
 *
 * @remarks
 * This enum is only used by `brokerageApiV2`. The legacy `brokerageApi` continues
 * to use string literals to maintain backward compatibility.
 * @example
 *
 * ```typescript
 * providesTags: result =>
 *   result ? [{ type: BrokerageTagTypes.Account, id: '123' }] : [];
 * ```
 */
export enum BrokerageTagTypes {
  Account = 'Account',
  Aggregator = 'Aggregator',
  Assets = 'Assets',
  Equity = 'Equity',
  Portfolio = 'Portfolio',
  Currency = 'Currency',
  Proposals = 'Proposals',
  Client = 'Client',
  Institution = 'Institution',
}

/**
 * Legacy Brokerage API configuration using Redux Toolkit Query.
 *
 * This is the original/legacy API used to interact with brokerage-related
 * endpoints. It uses string literals for tag types to maintain backward
 * compatibility.
 *
 * @remarks
 * **Note:** This API is being phased out in favor of `brokerageApiV2`. New
 * endpoints should use `brokerageApiV2` which uses the `BrokerageTagTypes` enum for
 * type safety.
 */
export const brokerageApi = createApi({
  baseQuery: fetchQuery({
    baseUrl: environment.API.BROKER_BASE_URL,
    authTokenUsage: 'header',
  }),
  endpoints: () => ({}),
  reducerPath: 'brokerageApi',
  tagTypes: [
    'Account',
    'Aggregator',
    'Assets',
    'Equity',
    'Portfolio',
    'Currency',
    'Proposals',
    'Client',
    ...SHARED_TAG_TYPES,
  ],
});

/**
 * Brokerage API V2 configuration using Redux Toolkit Query.
 *
 * This is the new API version that uses the `BrokerageTagTypes` enum for type-safe
 * cache tag management. All new endpoints should use this API.
 *
 * @remarks
 * **Key Features:**
 *
 * - Uses `BrokerageTagTypes` enum for type-safe tag management
 * - Includes `Institution` tag type (not available in legacy API)
 * - All raw endpoints in `raw-api.ts` use this API
 *
 * **Migration:** Legacy endpoints in `brokerageApi` should be gradually migrated to
 * this API to take advantage of type safety and the centralized tag enum.
 */
export const brokerageApiV2 = createApi({
  baseQuery: fetchQuery({
    baseUrl: environment.API.BROKER_BASE_URL,
    authTokenUsage: 'header',
  }),
  endpoints: () => ({}),
  reducerPath: 'brokerageApiV2',
  tagTypes: [
    BrokerageTagTypes.Account,
    BrokerageTagTypes.Aggregator,
    BrokerageTagTypes.Assets,
    BrokerageTagTypes.Equity,
    BrokerageTagTypes.Portfolio,
    BrokerageTagTypes.Currency,
    BrokerageTagTypes.Proposals,
    BrokerageTagTypes.Client,
    BrokerageTagTypes.Institution,
    ...SHARED_TAG_TYPES,
  ],
});
